/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { api } from "@app/data/api";
import {
  ClosingReportFilters,
  ClosingReportsPageFilter,
} from "./components/ClosingReportFilters";
import { AddClosingReportModal } from "./components/AddClosingReportModal";
import { ClosingReportDetails } from "./components/ClosingReportDetails";
import { getDateRange } from "@app/vue/components/UIComponents";
import { useToast } from "@app/vue/providers/Toast";
import { reportsAPI } from "@app/data/api/reports";
import { useAuth } from "@app/vue/providers/Auth";
import {
  useLocalization,
  PageHeader,
  ResponsiveTable,
  ResponsiveTableColumn,
} from "@lemmonhq/uikit";
import { formatDate } from "@lemmonhq/orders";
import { ClosingReport } from "@lemmonhq/types";

const formatCHF = (amount: number) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
  }).format(amount ?? 0);

const diffClass = (v: number) =>
  v > 0 ? "text-success" : v < 0 ? "text-error" : "text-base-content/50";

const BalanceRow = ({
  icon,
  label,
  amount,
  isDiff,
}: {
  icon: string;
  label: string;
  amount: number;
  isDiff?: boolean;
}) => (
  <div class="flex items-center justify-between gap-2 text-xs">
    <span class="flex items-center gap-1 text-base-content/60">
      <span class="material-symbols-outlined text-sm">{icon}</span>
      {label}
    </span>
    <span class={`tabular-nums font-medium ${isDiff ? diffClass(amount) : ""}`}>
      {formatCHF(amount)}
    </span>
  </div>
);

const METHOD_META: Record<string, { icon: string; label: string }> = {
  cash: { icon: "payments", label: "Cash" },
  card: { icon: "credit_card", label: "Card" },
  manual: { icon: "receipt_long", label: "Manual" },
};

export const ClosingReportsPage = defineComponent({
  name: "ClosingReportsPage",
  setup() {
    const { t } = useLocalization();
    const { showToast } = useToast();
    const auth = useAuth();

    const loading = ref(true);
    const reports = ref<any[]>([]);
    const showAddModal = ref(false);
    const selectedReport = ref<any | null>(null);
    const isDetailsOpen = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const isSavingReport = ref(false);

    const dateRange = getDateRange("today");
    const filters = ref<ClosingReportsPageFilter>({
      userId: undefined,
      dateRange: { type: "today", ...dateRange },
    });

    const activeFilterCount = computed(() => {
      let n = 0;
      if (filters.value.dateRange.type !== "today") n++;
      return n;
    });

    const filterSummary = computed(() =>
      filters.value.dateRange.type.replace(/_/g, " "),
    );

    const fetchReports = async () => {
      loading.value = true;
      try {
        const response = await reportsAPI.getClosingReports({
          page: currentPage.value,
          limit: 10,
          ...filters.value,
        });
        reports.value = response.data;
        totalPages.value = response.pagination.total_pages;
      } catch (error) {
        showToast("Failed to fetch closing reports", { type: "error" });
      } finally {
        loading.value = false;
      }
    };

    const handleAddReport = async (data: {
      starting_balances: any;
      closing_balances: any;
      differences: any;
      notes: string;
      shift_id: string;
    }) => {
      try {
        isSavingReport.value = true;
        await api.createClosingReport(data);
        showToast("Closing report created successfully", { type: "success" });
        fetchReports();
      } catch {
        showToast("Failed to create closing report", { type: "error" });
      } finally {
        isSavingReport.value = false;
      }
    };

    const handleDeleteReport = async (id: string) => {
      if (!confirm("Are you sure you want to delete this report?")) return;
      try {
        await api.deleteClosingReport(id);
        showToast("Closing report deleted", { type: "success" });
        fetchReports();
      } catch {
        showToast("Failed to delete closing report", { type: "error" });
      }
    };

    const handleFilterChange = (newFilters: ClosingReportsPageFilter) => {
      filters.value = newFilters;
      currentPage.value = 1;
      fetchReports();
    };

    onMounted(() => fetchReports());

    const columns: ResponsiveTableColumn[] = [
      // ── Desktop: Staff / Date ─────────────────────────────────────────────
      {
        key: "d-staff-date",
        label: "Staff / Date",
        icon: "person",
        mobilePriority: "hidden",
        render: (r: ClosingReport) => (
          <div class="flex flex-col gap-0.5">
            <span class="font-medium text-accent">{r.shift.user.name}</span>
            <span class="text-xs text-base-content/50">
              {formatDate(r.shift.start)}
              {r.shift.end ? ` → ${formatDate(r.shift.end)}` : ""}
            </span>
            <span class="text-xs text-base-content/35">
              {formatDate(r.created_at)}
            </span>
          </div>
        ),
      },
      // ── Desktop: Starting ────────────────────────────────────────────────
      {
        key: "d-starting",
        label: "Starting",
        icon: "account_balance",
        mobilePriority: "hidden",
        render: (r: ClosingReport) => (
          <div class="flex flex-col gap-1">
            <BalanceRow
              icon="payments"
              label="Cash"
              amount={r.starting_balances?.cash ?? 0}
            />
          </div>
        ),
      },
      // ── Desktop: Closing ─────────────────────────────────────────────────
      {
        key: "d-closing",
        label: "Closing",
        icon: "lock",
        mobilePriority: "hidden",
        render: (r: ClosingReport) => (
          <div class="flex flex-col gap-1">
            {["cash", "card", "manual"].map((m) => {
              const meta = METHOD_META[m];
              const amt = r.closing_balances?.[m] ?? 0;
              return amt !== 0 ? (
                <BalanceRow
                  key={m}
                  icon={meta.icon}
                  label={meta.label}
                  amount={amt}
                />
              ) : null;
            })}
          </div>
        ),
      },
      // ── Desktop: Differences ─────────────────────────────────────────────
      {
        key: "d-diff",
        label: "Difference",
        icon: "compare_arrows",
        mobilePriority: "hidden",
        render: (r: ClosingReport) => (
          <div class="flex flex-col gap-1">
            {["cash", "card", "manual"].map((m) => {
              const meta = METHOD_META[m];
              const amt = r.differences?.[m] ?? 0;
              return amt !== 0 ? (
                <BalanceRow
                  key={m}
                  icon={meta.icon}
                  label={meta.label}
                  amount={amt}
                  isDiff
                />
              ) : null;
            })}
          </div>
        ),
      },
      // ── Desktop: Notes ───────────────────────────────────────────────────
      {
        key: "d-notes",
        label: "Notes",
        icon: "notes",
        mobilePriority: "hidden",
        cellClass: "max-w-[200px]",
        render: (r: ClosingReport) =>
          r.notes ? (
            <span class="text-sm text-base-content/70 line-clamp-2">
              {r.notes}
            </span>
          ) : (
            <span class="text-base-content/25">—</span>
          ),
      },
      // ── Desktop: Delete action ───────────────────────────────────────────
      {
        key: "d-actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right",
        render: (r: ClosingReport) => (
          <button
            class="btn btn-sm btn-ghost text-error"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handleDeleteReport(r.id);
            }}
          >
            <span class="material-symbols-outlined text-base">delete</span>
          </button>
        ),
      },

      // ═══════════════════════════════════════════════════════════════════════
      // MOBILE columns
      // ═══════════════════════════════════════════════════════════════════════

      // primary: staff + created date
      {
        key: "m-staff",
        label: "Staff",
        desktopHide: true,
        mobilePriority: "primary",
        render: (r: ClosingReport) => (
          <span class="font-semibold text-accent text-sm">
            {r.shift.user.name}
          </span>
        ),
      },
      {
        key: "m-date",
        label: "Date",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center justify-end",
        render: (r: ClosingReport) => (
          <span class="text-xs text-base-content/50">
            {formatDate(r.created_at)}
          </span>
        ),
      },

      // secondary: shift window, cash start/close/diff
      {
        key: "m-shift",
        label: "Shift",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (r: ClosingReport) => (
          <span>
            {formatDate(r.shift.start)}
            {r.shift.end ? ` → ${formatDate(r.shift.end)}` : ""}
          </span>
        ),
      },
      {
        key: "m-cash-start",
        label: "Starting Cash",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (r: ClosingReport) => (
          <span class="tabular-nums">
            {formatCHF(r.starting_balances?.cash ?? 0)}
          </span>
        ),
      },
      {
        key: "m-cash-close",
        label: "Closing Cash",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (r: ClosingReport) => (
          <span class="tabular-nums">
            {formatCHF(r.closing_balances?.cash ?? 0)}
          </span>
        ),
      },
      {
        key: "m-diff",
        label: "Difference",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (r: ClosingReport) => {
          const total = ["cash", "card", "manual"].reduce(
            (s, m) => s + (r.differences?.[m] ?? 0),
            0,
          );
          return (
            <span class={`tabular-nums font-medium ${diffClass(total)}`}>
              {formatCHF(total)}
            </span>
          );
        },
      },
    ];

    return () => (
      <div class="w-full flex flex-col h-full overflow-y-auto">
        <PageHeader
          title={t("reports.closing_reports.title")}
          filterSummary={filterSummary.value}
          activeFilterCount={activeFilterCount.value}
          filterSheetTitle="Filters"
        >
          {{
            default: ({
              closeMobileSheet,
            }: {
              closeMobileSheet: () => void;
            }) => (
              <ClosingReportFilters
                filters={filters.value}
                onFilterChange={handleFilterChange}
                users={auth.user.value?.colleagues || []}
                onApply={closeMobileSheet}
              />
            ),
            actions: () => (
              <button
                class="btn btn-sm btn-primary"
                onClick={() => (showAddModal.value = true)}
              >
                <span class="material-symbols-outlined text-base">add</span>
                <span class="hidden md:inline">New Report</span>
              </button>
            ),
          }}
        </PageHeader>

        <div class="flex-1 px-2 xl:px-4 pb-4 pt-2">
          <ResponsiveTable
            columns={columns}
            rows={reports.value}
            rowKey={(r: any) => r.id}
            onRowClick={(r: any) => {
              selectedReport.value = r;
              isDetailsOpen.value = true;
            }}
            loading={loading.value}
            emptyMessage="No closing reports found — create one to get started"
          />

          {totalPages.value > 1 && (
            <div class="flex justify-center mt-6">
              <div class="join">
                {Array.from({ length: totalPages.value }, (_, i) => (
                  <button
                    key={i}
                    class={`join-item btn btn-sm ${currentPage.value === i + 1 ? "btn-active" : ""}`}
                    onClick={() => {
                      currentPage.value = i + 1;
                      fetchReports();
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {showAddModal.value && (
          <AddClosingReportModal
            onClose={() => (showAddModal.value = false)}
            onSubmit={handleAddReport}
            isSavingReport={isSavingReport.value}
          />
        )}

        {selectedReport.value && (
          <ClosingReportDetails
            report={selectedReport.value}
            isOpen={isDetailsOpen.value}
            onClose={() => {
              isDetailsOpen.value = false;
              selectedReport.value = null;
            }}
            onDelete={(id: string) => {
              isDetailsOpen.value = false;
              selectedReport.value = null;
              handleDeleteReport(id);
            }}
          />
        )}
      </div>
    );
  },
});
